/**
 * The board's tab bar: the server's catalog, in the server's order, each tab
 * with its Monday colour as a bar, its label and the count the server
 * computed from the same predicate as the rows.
 *
 * A real ARIA tablist: one tab in the Tab order (the selected one), arrows /
 * Home / End move and select, Enter and Space select. A catch-all tab marked
 * `hide_when_empty` appears only when something lands in it (or when it is
 * the selected one, so a link to it never shows nothing selected).
 */
import React, { useRef } from 'react';
import { Marker } from '../tide';
import type { BoardTabDef } from './types';

export interface BoardTabsProps {
  tabs: BoardTabDef[];
  value: string;
  onChange: (key: string) => void;
  label: string;
  /** Prefix for tab ids; the tab panel is `${idBase}-panel`. */
  idBase: string;
}

export const tabId = (idBase: string, key: string) => `${idBase}-tab-${key}`;
export const panelId = (idBase: string) => `${idBase}-panel`;

export function visibleTabs(tabs: BoardTabDef[], value: string): BoardTabDef[] {
  return tabs.filter((t) => !(t.hide_when_empty && (t.count ?? 0) === 0 && t.key !== value));
}

export function TabSwatch({ color }: { color: string | null }) {
  return <span className={`board-swatch${color ? '' : ' board-swatch--none'}`} style={color ? { background: color } : undefined} aria-hidden="true" />;
}

export default function BoardTabs({ tabs, value, onChange, label, idBase }: BoardTabsProps) {
  const shown = visibleTabs(tabs, value);
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});
  const move = (from: number, to: number) => {
    const n = shown.length;
    if (!n) return;
    const t = shown[((to % n) + n) % n];
    onChange(t.key);
    refs.current[t.key]?.focus();
    void from;
  };
  const onKey = (e: React.KeyboardEvent, i: number) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); move(i, i + 1); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); move(i, i - 1); }
    else if (e.key === 'Home') { e.preventDefault(); move(i, 0); }
    else if (e.key === 'End') { e.preventDefault(); move(i, shown.length - 1); }
  };
  const selectedShown = shown.some((t) => t.key === value);
  return (
    <div className="board-tabs" role="tablist" aria-label={label}>
      {shown.map((t, i) => {
        const on = t.key === value;
        const focusable = on || (!selectedShown && i === 0);
        return (
          <button
            key={t.key}
            ref={(el) => { refs.current[t.key] = el; }}
            id={tabId(idBase, t.key)}
            type="button"
            role="tab"
            aria-selected={on}
            aria-controls={panelId(idBase)}
            tabIndex={focusable ? 0 : -1}
            className="board-tab"
            data-source={t.source}
            title={t.what}
            onClick={() => onChange(t.key)}
            onKeyDown={(e) => onKey(e, i)}
          >
            {t.marker
              ? <span className="board-tabmark" aria-hidden="true"><Marker shape={t.marker} color={t.color || 'var(--tide-status-missing)'} size={10} /></span>
              : t.source !== 'EMERGE' || t.color ? <TabSwatch color={t.color} /> : null}
            <span className="board-tab__label">{t.label}</span>
            {typeof t.count === 'number' && <span className="board-tab__count">{t.count.toLocaleString('en-US')}</span>}
          </button>
        );
      })}
    </div>
  );
}
